import type { Chapter } from '../api/types';
import type { PlayerErrorAction, PlayerErrorCode } from './engine';

/**
 * How a seek was requested. History logging maps these to Flutter's event
 * types (seek, jumpBackward/Forward, chapterSelected/Next/Previous).
 * `serverSync` is a resumed book moving to where another device left it.
 */
export type SeekKind = 'seek' | 'skipBack' | 'skipForward' | 'chapterSelect' | 'chapterNext' | 'chapterPrevious' | 'smartRewind' | 'serverSync';

export type SleepCancelReason = 'user' | 'expired' | 'finished';

/** Every event the player emits, with its payload. Positions are seconds from the start of the book. */
export interface PlayerEventMap {
  /** A book's audio source is attached and ready (a new playback session began). */
  sessionStarted: { itemId: string; source: 'stream' | 'local'; startAt: number; duration: number; title: string };
  /** Playback intent switched on (the element started, or is starting, to play). */
  play: { itemId: string; position: number };
  /**
   * Playback stopped for any reason. `stopped` is true when the player went
   * idle (Stop, switching books, reaching the end) rather than a plain pause.
   */
  pause: { itemId: string; position: number; stopped: boolean };
  seek: { itemId: string; from: number; to: number; kind: SeekKind; chapterIndex: number; chapter: Chapter | null };
  /** The chapter under the playhead changed (indices into `PlayerState.chapters`, -1 when none). */
  chapterChanged: { itemId: string; from: number; to: number };
  /**
   * `transient` marks intermediate values while a slider is being dragged.
   * Non-transient calls always emit, even when the rate is unchanged, so a
   * listener can settle a drag whose last transient value already applied it.
   * `silent` marks a rate that follows a scope toggle rather than a speed
   * choice (Flutter logs no history for it).
   */
  rateChanged: { itemId: string | null; from: number; to: number; scope: 'book' | 'all'; transient: boolean; silent?: boolean };
  /**
   * `durationMs` is the countdown length, or (book time) the time left until
   * the target chapter's end for end-of-chapter timers; `chapterIndex` is that
   * chapter (absent when the book has no chapters).
   */
  sleepTimerSet: { itemId: string | null; mode: 'timer' | 'chapter'; durationMs: number; endsAt: number | null; chapterIndex?: number };
  sleepTimerCancelled: { itemId: string | null; reason: SleepCancelReason };
  sleepTimerFired: { itemId: string | null };
  bookFinished: { itemId: string; duration: number };
  /** Lock-screen style stop: the source was released (also fired by `close()`). */
  stop: { itemId: string | null; position: number };
  bufferingStart: { itemId: string; position: number };
  bufferingEnd: { itemId: string; position: number };
  /**
   * `fatal` is true when playback gave up (status `error`), false for
   * recoverable notices. `code` / `action` classify it (see `PlayerError` in engine.ts).
   */
  error: { itemId: string | null; message: string; fatal: boolean; code: PlayerErrorCode; action: PlayerErrorAction };
}

export type PlayerEventType = keyof PlayerEventMap;
export type PlayerEventListener<K extends PlayerEventType> = (payload: PlayerEventMap[K]) => void;

/**
 * Small typed emitter. Listeners run synchronously; a throwing listener is
 * reported and never interrupts playback or the other listeners.
 */
export class PlayerEvents {
  private readonly listeners = new Map<PlayerEventType, Set<(payload: never) => void>>();

  on<K extends PlayerEventType>(type: K, listener: PlayerEventListener<K>): () => void {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    set.add(listener as (payload: never) => void);
    return () => this.off(type, listener);
  }

  off<K extends PlayerEventType>(type: K, listener: PlayerEventListener<K>): void {
    this.listeners.get(type)?.delete(listener as (payload: never) => void);
  }

  /** Subscribes to every event type; the listener receives the type and payload. */
  onAny(listener: <K extends PlayerEventType>(type: K, payload: PlayerEventMap[K]) => void): () => void {
    const offs = EVENT_TYPES.map((type) => this.on(type, (payload) => listener(type, payload)));
    return () => offs.forEach((off) => off());
  }

  emit<K extends PlayerEventType>(type: K, payload: PlayerEventMap[K]): void {
    const set = this.listeners.get(type);
    if (!set || set.size === 0) return;
    for (const listener of [...set]) {
      try {
        (listener as PlayerEventListener<K>)(payload);
      } catch (err) {
        console.error(`[player] ${type} listener failed`, err);
      }
    }
  }

  listenerCount(type?: PlayerEventType): number {
    if (type) return this.listeners.get(type)?.size ?? 0;
    let n = 0;
    this.listeners.forEach((set) => (n += set.size));
    return n;
  }
}

export const EVENT_TYPES: readonly PlayerEventType[] = [
  'sessionStarted',
  'play',
  'pause',
  'seek',
  'chapterChanged',
  'rateChanged',
  'sleepTimerSet',
  'sleepTimerCancelled',
  'sleepTimerFired',
  'bookFinished',
  'stop',
  'bufferingStart',
  'bufferingEnd',
  'error',
];
